import React, {Fragment} from 'react'
import {Button, Dialog, DialogContent} from '@material-ui/core'
import {useTranslation} from 'react-i18next'
import useStyles from 'components/Dialog/Product/CancelProductDialog/style'
import {SubmitHandler, useForm} from 'react-hook-form'
import {
  Payment,
  cancelPaymentApi,
  ProductPurchaseHistoryResponse,
} from 'api/productApi'
import {UNIT_PRICES} from 'pages/ProductsPages/constant'
import {AppLoading} from 'AppLoading'
import useFailureModal from 'features/modal/useFailureModal'
import {formatPriceNumber} from 'helpers/uiHelper'
import {usdToCent} from 'helpers/paymentHelper'

interface CancelProductDialogProps {
  isOpen: boolean
  onClose: () => void
  relatedPayments: Payment[]
  product: ProductPurchaseHistoryResponse | null
  onCancelPayment: () => void
}

const CancelProductDialog = ({
  isOpen,
  onClose,
  product,
  relatedPayments,
  onCancelPayment,
}: CancelProductDialogProps) => {
  const {t} = useTranslation()
  const classes = useStyles()
  const [loading, setLoading] = React.useState(false)
  const {register, handleSubmit, watch} = useForm({
    defaultValues: {
      reason: '',
    },
  })
  const watchReason = watch('reason')
  const {onOpen: onOpenFailureModal} = useFailureModal()

  const cancelPayment: SubmitHandler<{
    reason: string
  }> = async (data) => {
    try {
      setLoading(true)
      const res = await cancelPaymentApi({
        paymentId: product?.payment.paymentId || '',
        reason: data.reason,
      })
      if (res.msg === 'SUCCESS') {
        onCancelPayment()
      }
    } catch (error) {
      onOpenFailureModal(error.message)
    } finally {
      onClose()
      setLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onClose={onClose} className={classes.container}>
      <DialogContent className={classes.contentWrap}>
        {loading ? (
          <AppLoading loading />
        ) : (
          <Fragment>
            <h2 className={classes.productTitle}>{t('ICancelPayment')}</h2>
            <div className={classes.productWrapper}>
              <p>
                * {t('IOrderNo')}:{' '}
                <div
                  style={{
                    color: '#dc2626',
                    fontWeight: 600,
                  }}
                >
                  {product?.payment.paymentNo}
                </div>
              </p>
              <p>
                * {t('IBuyer')}:{' '}
                <div
                  style={{
                    color: '#dc2626',
                    fontWeight: 600,
                  }}
                >
                  {`${product?.payment.customer.name}`}
                </div>
              </p>
              <p>
                * {t('IPhone')}:{' '}
                <div
                  style={{
                    color: '#dc2626',
                    fontWeight: 600,
                  }}
                >
                  {product?.payment.customer.phoneNumber}{' '}
                </div>
              </p>
              <p>
                * {t('IEmail')}:{' '}
                <div
                  style={{
                    color: '#dc2626',
                    fontWeight: 600,
                  }}
                >
                  {product?.payment.customer.email}{' '}
                </div>
              </p>
              <p>
                * {t('IPaymentMethod')}:{' '}
                <div
                  style={{
                    color: '#dc2626',
                    fontWeight: 600,
                  }}
                >
                  {product?.payment.paymentType}
                </div>
              </p>
              <p>
                * {t('IAmount')}:{' '}
                <div
                  style={{
                    color: '#dc2626',
                    fontWeight: 600,
                  }}
                >
                  {product && product?.order.price.currency}{' '}
                  {product &&
                    formatPriceNumber(
                      product?.order.price.currency === UNIT_PRICES.USD
                        ? usdToCent(product?.order.price.price)
                        : product?.order.price.price,
                    )}
                </div>
              </p>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <p>* {t('IReason')}:</p>
                <input
                  {...register('reason')}
                  className={classes.reasonInput}
                />
              </div>
            </div>
            {relatedPayments.length > 0 && (
              <div className={classes.productWrapper}>
                <p>※ {t('ICancelRechargeDesc')}</p>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 5,
                    overflow: 'auto',
                    maxHeight: 150,
                  }}
                >
                  {relatedPayments.map((item) => (
                    <p key={item.paymentId}>
                      * {t('IOrderNo')}: {item.paymentNo}
                    </p>
                  ))}
                </div>
              </div>
            )}
            <div className={classes.buttonWrapper}>
              <Button onClick={onClose} variant='contained' color='primary'>
                {t('ICancel')}
              </Button>
              <Button
                onClick={handleSubmit(cancelPayment)}
                disabled={!watchReason.length}
                variant='contained'
                color='primary'
              >
                {t('IConfirm')}
              </Button>
            </div>
          </Fragment>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default CancelProductDialog
